import { reduce } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, switchMap, share, first, map, finalize, take } from 'rxjs/operators';
import { StsSelectors } from 'src/app/@store/selectors/sts.selectors';
import { claimValues } from 'src/app/appsettings';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ChangeEmailService } from 'src/app/services/change-email.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';

const CONFIRM_EMAIL_API = '/manage/accounts/v3/confirm-email';

@Component({
  selector: 'syn-activate-account',
  templateUrl: './activate-account.component.html',
  styleUrls: ['./activate-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  success$: Observable<boolean> = this.activeRt.queryParamMap.pipe(
    first(),
    share(),
    switchMap(params =>
      this.putConfirmEmail(
        params.get('sequenceId') || params.get('seqId'),
        params.get('id'),
        params.get('change-email-verify'),
      ),
    ),
    finalize(() =>
      combineLatest([this.isSHFYInstance$, this.isITInstance$, this.isGlobal$])
        .pipe(take(1))
        .subscribe(([isShfy, isIT, isGlobal]) =>
          isShfy || isIT || isGlobal ? this.shfyActivationRedirect() : this.loading$.next(false),
        ),
    ),
  );
  private hasRedirected: boolean = false;

  error$: BehaviorSubject<string | null> = new BehaviorSubject(null);

  loginLink$: Observable<string> = this.config.envConfig$.pipe(map(c => c.BaseAuthUrl));
  isSHFYInstance$ = this.store.select(StsSelectors.isUkShfyInstance);
  isITInstance$ = this.store.select(StsSelectors.isITTenant);
  isGlobal$ = this.store.select(StsSelectors.isGlobalTenant);
  isActiveUser: boolean = false;
  isLinkExpired: boolean = false;
  isDuplicateError: boolean = false;

  constructor(
    private readonly store: Store,
    private readonly activeRt: ActivatedRoute,
    private readonly api: ApiService,
    private readonly config: ConfigService,
    private readonly changeEmail: ChangeEmailService,
    private readonly auth: AuthService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.errorHandlerService.disableError = true;
  }

  ngOnDestroy(): void {
    this.errorHandlerService.disableError = false;
  }

  // eslint-disable-next-line max-lines-per-function
  private putConfirmEmail(seqId: string, id: string, isChangeEmail?: string): Observable<boolean> {
    isChangeEmail = <any>(isChangeEmail?.toLowerCase() === 'true');
    const endpoint$ = isChangeEmail
      ? this.changeEmail.verifyEmailLink(id, seqId)
      : this.api.put(CONFIRM_EMAIL_API, { id, seqId, sequenceId: seqId });

    return endpoint$.pipe(
      map(res => {
        let result = `${res}`.toLowerCase() === claimValues.TRUE;
        if (isChangeEmail) {
          document.body.classList.add('change-email-veriifed');
          if (res?.success) {
            result = true;
          } else {
            result = false;
            this.error$.next(res?.message);
          }
        }
        return result;
      }),
      catchError((err: HttpErrorResponse) => {
        combineLatest([this.isSHFYInstance$, this.isITInstance$, this.isGlobal$])
          .pipe(take(1))
          .subscribe(([isShfy, isIt, isGlobal]) => {
            document.body.classList.add('change-email-veriifed');
            const error = err.error || (err as any).Error;
            const errorCode = error.errorCode || error.ErrorCode;
            this.isActiveUser = errorCode === 'SYNERR-0020';
            this.isLinkExpired = errorCode === 'SYNERR-0065';
            this.isDuplicateError = errorCode === 'SYNERR-0062';

            if (isShfy || isIt || isGlobal) {
              if (this.isActiveUser) {
                this.shfyActivationRedirect('is-active-user');
              } else if (this.isLinkExpired) {
                this.shfyActivationRedirect('link-expired');
              } else if (this.isDuplicateError) {
                this.shfyActivationRedirect('is-duplicate-fiscal-code');
              }
            }

            this.error$.next(err.error?.message);
          });
        return of(false);
      }),
    );
  }

  private shfyActivationRedirect(status: string = 'success') {
    if (!this.hasRedirected) {
      this.hasRedirected = true;
      window.location.href = `${this.config.baseAuthUrl}/Account/Login?activation-status=${status}`;
    }
  }

  redirectToLogin(): void {
    this.auth.startSignoutMainWindow();
  }
}
